export const PLATFORMS = Object.freeze({
  GOOGLE: "GOOGLE",
  FACEBOOK: "FACEBOOK",
  DEALER_RATER: "DEALER_RATER",
  YELP: "YELP",
  CARS_COM: "CARS_COM",
});

export const STAGE = Object.freeze({
  INVITE_SENT: "invite_sent",
  INVITE_OPENED: "invite_opened",
  GOOD: "good",
  BAD: "bad",
  FEEDBACK_SUBMITTED: "feedback_submitted",
  OTTO_USED: "otto_used",
  SELF_WRITTEN: "self_written",
  PLATFORM_CLICKED: "platform_clicked",
  REVIEW_POSTED: "review_posted",
  UNKNOWN: "unknown",
});

export const PLATFORM_LABELS = {
  [PLATFORMS.GOOGLE]: "Google",
  [PLATFORMS.FACEBOOK]: "Facebook",
  [PLATFORMS.DEALER_RATER]: "DealerRater",
  [PLATFORMS.YELP]: "Yelp",
  [PLATFORMS.CARS_COM]: "Cars.com",
};

const PLATFORM_SET = new Set(Object.values(PLATFORMS));

// (actionType|actionValue) → stage key. Used by getTrackingStagesFromDetails.
const PAIR_TO_STAGE = {
  "invite|": STAGE.INVITE_SENT,
  "VIEW|START": STAGE.INVITE_OPENED,
  "CLICK|GOOD": STAGE.GOOD,
  "CLICK|BAD": STAGE.BAD,
  "CLICK|SUGGEST_MESSAGE_GEN": STAGE.OTTO_USED,
  "CLICK|SUGGEST_MESSAGE_COPY": STAGE.SELF_WRITTEN,
  "SUBMIT|BAD": STAGE.FEEDBACK_SUBMITTED,
  ...Object.values(PLATFORMS).reduce((acc, p) => {
    acc[`CLICK|${p}`] = STAGE.PLATFORM_CLICKED;
    acc[`SUBMIT|${p}`] = STAGE.REVIEW_POSTED;
    return acc;
  }, {}),
};

// Plain-token → stage key. Used by getTrackingStagesFromActions (flat string[] shape).
const TOKEN_TO_STAGE = {
  INVITED: STAGE.INVITE_OPENED,
  START: STAGE.INVITE_OPENED,
  GOOD: STAGE.GOOD,
  BAD: STAGE.BAD,
  SUGGEST_MESSAGE_GEN: STAGE.OTTO_USED,
  SUGGEST_MESSAGE_COPY: STAGE.SELF_WRITTEN,
};

// ---------------------------------------------------------------------------
// Table filter catalogs (Status / Last activity columns)
//
// The backend `getGroupedReviewTrackingDetails` query matches these tokens,
// case-insensitively, against the raw `action_type` / `action_value` columns
// (see WebApp-API/api/utils/review_tracking.py):
//   • `status`        → any record in the group (parent + children via request_id)
//   • `last_activity` → only the *latest* record per invite (shorten_url_id)
// Server-side expanders: status `review_posted` → all platforms, status
// `invited` → invite/start, last_activity `good` → also platform clicks.
// Each option's `value` is the exact backend token; the selected array is sent
// verbatim. Labels reuse the STAGE vocabulary above so the filter reads the
// same as the column icons/text.
// ---------------------------------------------------------------------------
export const REVIEW_STATUS_FILTER_OPTIONS = [
  { label: "Invite sent", value: "invite" },
  { label: "Invite opened", value: "start" },
  { label: "Positive experience", value: "good" },
  { label: "Negative experience", value: "bad" },
  { label: "Review drafted by Otto", value: "suggest_message_gen" },
  { label: "Self written review", value: "suggest_message_copy" },
  { label: "Review posted", value: "review_posted" },
];

// "Review posted" is intentionally omitted here: as a *last* activity it maps to
// a SUBMIT+platform record indistinguishable from a plain platform click, so the
// backend has no dedicated token for it (unlike the status `review_posted`
// expander). Platform clicks below cover the review-site dimension.
// Values are the canonical last-activity tokens the backend derives from a
// row's cleaned journey (see filter_grouped_by_journey / _last_activity_token).
// They mirror this file's getLastActivity() priority so the filter matches the
// "Last activity" column exactly.
export const REVIEW_LAST_ACTIVITY_FILTER_OPTIONS = [
  { label: "Invite sent", value: "invite" },
  { label: "Invite opened", value: "invited" },
  { label: "Positive experience", value: "good" },
  { label: "Negative experience", value: "bad" },
  { label: "AI generated review", value: "suggest_message_gen" },
  { label: "Self written review", value: "self_written" },
  { label: "Review posted", value: "review_posted" },
  { label: "Clicked on Google", value: "google" },
  { label: "Clicked on Facebook", value: "facebook" },
  { label: "Clicked on Yelp", value: "yelp" },
  { label: "Clicked on DealerRater", value: "dealer_rater" },
  { label: "Clicked on Cars.com", value: "cars_com" },
];

export const getTrackingStagesFromDetails = details => {
  if (!Array.isArray(details)) return [];
  return details
    .map((item, idx) => {
      if (!item || typeof item !== "object") return null;
      const { id, actionType, actionValue, createTs } = item;
      if (!actionType) return null;

      const uid = id != null ? `id_${id}` : `idx_${idx}`;
      const at = actionType.toLowerCase() === "invite" ? "invite" : actionType.toUpperCase();
      const av = actionValue ? actionValue.toUpperCase() : "";
      const ts = createTs || null;

      const key = PAIR_TO_STAGE[`${at}|${av}`] || STAGE.UNKNOWN;
      const platform = PLATFORM_SET.has(av) ? av : undefined;

      return { uid, key, platform, actionType: at, actionValue: av, timestamp: ts };
    })
    .filter(Boolean);
};

export const getTrackingStagesFromActions = actions => {
  if (!Array.isArray(actions)) return [];
  return actions
    .map((raw, idx) => {
      if (typeof raw !== "string") return null;
      const token = raw.toLowerCase() === "invite" ? "invite" : raw.toUpperCase();
      const uid = `idx_${idx}`;

      if (token === "invite") {
        return {
          uid,
          key: STAGE.INVITE_SENT,
          actionType: "invite",
          actionValue: "",
          timestamp: null,
        };
      }
      if (PLATFORM_SET.has(token)) {
        return {
          uid,
          key: STAGE.PLATFORM_CLICKED,
          platform: token,
          actionType: "CLICK",
          actionValue: token,
          timestamp: null,
        };
      }
      const stageKey = TOKEN_TO_STAGE[token] || STAGE.UNKNOWN;
      return { uid, key: stageKey, actionType: token, actionValue: "", timestamp: null };
    })
    .filter(Boolean);
};
